import { type AnyObject } from '@Types';
import { type auto } from 'angular';
import {
    type FrontRoyalStore,
    type BookmarkedStream,
    type StreamProgressRecord,
    type LessonProgressRecord,
    type ProjectProgressRecord,
} from 'FrontRoyalStore';
import memoizeOne from 'memoize-one';
import fetchProgressForUser from './fetchProgressForUser';

const buildFavoriteStreamsSet = memoizeOne((bookmarkedStreams: BookmarkedStream[]) =>
    bookmarkedStreams.reduce<AnyObject<boolean>>((obj, stream) => ({ ...obj, [stream.locale_pack_id]: true }), {}),
);

// Do not convert this to named args or you will break memoization
const buildResponse = memoizeOne(
    (
        streamProgressRecords: StreamProgressRecord[],
        lessonProgressRecords: LessonProgressRecord[],
        projectProgressRecords: ProjectProgressRecord[],
        favoriteStreamsSet: AnyObject<boolean>,
    ) => ({
        streamProgress: streamProgressRecords,
        lessonProgress: lessonProgressRecords,
        projectProgress: projectProgressRecords,
        favoriteStreamsSet,
    }),
);

export default async function ensureUserProgressFetched(userId: string, $injector: auto.IInjectorService) {
    const frontRoyalStore = $injector.get<FrontRoyalStore>('frontRoyalStore');
    const allProgressForUser = (await frontRoyalStore.retryRequestOnHandledError('allProgressForUser', userId))!;

    let streamProgressRecords = [];
    let lessonProgressRecords = [];
    let projectProgressRecords = [];

    if (allProgressForUser.fetched) {
        ({ streamProgressRecords, lessonProgressRecords, projectProgressRecords } = allProgressForUser);
    } else {
        const $http = $injector.get('$http');
        ({ streamProgressRecords, lessonProgressRecords, projectProgressRecords } = await fetchProgressForUser(
            userId,
            frontRoyalStore,
            $http,
        ));
    }

    const bookmarkedStreams = (await frontRoyalStore.retryRequestOnHandledError('bookmarkedStreamsForUser', userId))!;
    const favoriteStreamsSet = buildFavoriteStreamsSet(bookmarkedStreams);

    return buildResponse(streamProgressRecords, lessonProgressRecords, projectProgressRecords, favoriteStreamsSet);
}
